/**
 * Owner: Masa Bayu
 * File: spsu-native-dropcap-face.ts
 * Date:Time: 2026-10-05 09:20
 * Description: Face dropcap A+B · wrap inset lines in letter band only.
 */

import type { TextElement } from '../extract/spsu-types';

export type NativeDropCapPair = {
  readonly letterId: string;
  readonly hostId: string;
  readonly letter: TextElement;
  readonly host: TextElement;
  /** Inset lines beside the letter (same X as host) · Face flow only. */
  readonly absorbIds: readonly string[];
  readonly flowText: string;
};

/**
 * Strict Face-only dropcap:
 * A · 1 letter · fontSize ≥ 1.5× B
 * B · long text · |Δy| ≤ 5pt · |gap to A.right| ≤ 2pt
 * Optional wrap: following inset lines (same X as B) while still beside A.
 * Full-width / later paragraphs stay absolute LiveObject.
 */
export function detectNativeDropCapPairs(
  texts: readonly TextElement[],
): readonly NativeDropCapPair[] {
  if (texts.length < 2) return [];

  const used = new Set<string>();
  const pairs: NativeDropCapPair[] = [];

  for (const letter of texts) {
    if (used.has(letter.id)) continue;
    const glyph = letter.content.trim();
    if (glyph.length !== 1 || !/^\p{L}$/u.test(glyph)) continue;
    if (!(letter.style.fontSize > 0)) continue;

    let best: TextElement | null = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (const host of texts) {
      if (host.id === letter.id || used.has(host.id)) continue;
      const body = host.content.trim();
      if (body.length < 8) continue;
      if (!(host.style.fontSize > 0)) continue;
      if (letter.style.fontSize < host.style.fontSize * 1.5) continue;

      // Face top-y vs PDF baseline · Page 19 M/enjelang Δy≈6.55pt.
      const dy = Math.abs(host.bbox.y - letter.bbox.y);
      if (dy > 8) continue;

      const letterRight = letter.bbox.x + letter.bbox.width;
      const gap = host.bbox.x - letterRight;
      if (Math.abs(gap) > 2) continue;
      if (host.bbox.x <= letter.bbox.x) continue;

      const score = dy + Math.abs(gap);
      if (score < bestScore) {
        bestScore = score;
        best = host;
      }
    }

    if (!best) continue;
    used.add(letter.id);
    used.add(best.id);

    // Wrap companions: same inset column as host, still under the letter band.
    const bandBottom = letter.bbox.y + Math.max(letter.bbox.height, letter.style.fontSize);
    const byY = [...texts].sort((a, b) => a.bbox.y - b.bbox.y);
    const wrap: TextElement[] = [];
    let cursor = best;
    for (const cand of byY) {
      if (cand.id === letter.id || cand.id === best.id || used.has(cand.id)) continue;
      const ct = cand.content.trim();
      if (ct.length < 4) continue;
      if (Math.abs(cand.style.fontSize - best.style.fontSize) > 0.5) continue;
      if (Math.abs(cand.bbox.x - best.bbox.x) > 3) continue;
      if (cand.bbox.y <= cursor.bbox.y) continue;
      if (cand.bbox.y > bandBottom + best.style.fontSize * 0.35) break;
      wrap.push(cand);
      used.add(cand.id);
      cursor = cand;
    }

    const flowParts = [best.content.trim(), ...wrap.map((el) => el.content.trim())];
    pairs.push({
      letterId: letter.id,
      hostId: best.id,
      letter,
      host: best,
      absorbIds: wrap.map((el) => el.id),
      flowText: flowParts.join(' '),
    });
  }

  return pairs;
}

export function nativeDropCapSkipIds(
  pairs: readonly NativeDropCapPair[],
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const pair of pairs) {
    ids.add(pair.letterId);
    ids.add(pair.hostId);
    for (const id of pair.absorbIds) ids.add(id);
  }
  return ids;
}
